export const PAYMENT_TYPE = {
  CASH: "cash",
  CHEQUE: "cheque",
  ONLINE: "online",
  PAY_ORDER: "pay_order",
  CREDIT: "credit",
  BANK: "bank",
};

export const ACTIVE_PAYMENT_TYPES = [
  PAYMENT_TYPE.CASH,
  PAYMENT_TYPE.CHEQUE,
  PAYMENT_TYPE.ONLINE,
  PAYMENT_TYPE.PAY_ORDER,
  PAYMENT_TYPE.CREDIT,
];

export const SETTLE_PAYMENT_TYPES = [
  PAYMENT_TYPE.CASH,
  PAYMENT_TYPE.CHEQUE,
  PAYMENT_TYPE.ONLINE,
  PAYMENT_TYPE.PAY_ORDER,
];

export const PROOF_PAYMENT_TYPES = [PAYMENT_TYPE.CHEQUE, PAYMENT_TYPE.ONLINE, PAYMENT_TYPE.PAY_ORDER];

export const PLAN_STATUS = {
  PENDING: "pending",
  PARTIAL: "partial",
  PAID: "paid",
};

export const PAYMENT_LABEL = {
  cash: "Cash",
  cheque: "Cheque",
  online: "Online",
  pay_order: "Pay order",
  credit: "Udhar",
  bank: "Bank",
};

export const PLAN_STATUS_LABEL = {
  pending: "Due",
  partial: "Part paid",
  paid: "Paid",
};

export const paymentLabel = (type) => PAYMENT_LABEL[type] || type || "—";

export const requiresProof = (type) => PROOF_PAYMENT_TYPES.includes(type);

export const requiresBank = (type) => requiresProof(type);

export const isCreditPayment = (type) => type === PAYMENT_TYPE.CREDIT;

export const pickBankId = (item = {}) => {
  const value = item.bankId || item.bank || item.downPaymentBank;
  if (!value) return "";
  if (typeof value === "object") return String(value.id || value._id || "");
  return String(value);
};

export const bankLabel = (bank) => {
  if (!bank) return "";
  if (typeof bank === "string") return bank;
  return bank.name || "";
};

export const bankDetail = (bank) => {
  const name = bankLabel(bank);
  if (!name || typeof bank !== "object") return name;
  const extra = [bank.accountTitle, bank.accountNumber].filter(Boolean).join(" · ");
  return extra ? `${name} · ${extra}` : name;
};

export const proofNumberLabel = (type) => {
  if (type === PAYMENT_TYPE.CHEQUE) return "Cheque number";
  if (type === PAYMENT_TYPE.PAY_ORDER) return "Pay order number";
  if (type === PAYMENT_TYPE.ONLINE) return "Transaction ID";
  return "Reference";
};

export const paymentHint = (type) => {
  if (type === PAYMENT_TYPE.CREDIT) return "Udhar goes on the vendor's khata. Today's cash does not go down.";
  if (type === PAYMENT_TYPE.CHEQUE) return "Cheque number, bank, and picture are required.";
  if (type === PAYMENT_TYPE.ONLINE) return "Transaction ID, bank, and picture are required.";
  if (type === PAYMENT_TYPE.PAY_ORDER) return "Pay order number, bank, and picture are required.";
  if (type === PAYMENT_TYPE.BANK) return "Bank transfer — taken from today's cashbook.";
  return "Taken from today's cashbook.";
};

export const emptyProof = (paymentType = PAYMENT_TYPE.CASH) => ({
  paymentType,
  referenceNumber: "",
  image: "",
  bankId: "",
});

export const proofFromRecord = (item = {}) => ({
  paymentType: item.paymentType || item.downPaymentType || PAYMENT_TYPE.CASH,
  referenceNumber: item.referenceNumber || item.downPaymentReference || "",
  image: item.image || item.downPaymentImage || "",
  bankId: pickBankId(item),
});

export const withProof = (body, form = {}) => {
  const next = { ...body, paymentType: form.paymentType };
  if (requiresProof(form.paymentType)) {
    next.referenceNumber = (form.referenceNumber || "").trim();
    next.image = form.image || "";
    next.bankId = form.bankId || undefined;
  }
  return next;
};

export const setPaymentFields = (form, paymentType) =>
  requiresProof(paymentType)
    ? { ...form, paymentType }
    : { ...form, paymentType, referenceNumber: "", image: "", bankId: "" };

export const pad2 = (value) => String(value).padStart(2, "0");

export const isoDate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export const isoMonth = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
};

export const addMonths = (iso, count) => {
  if (!iso) return "";
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1 + Number(count || 0), day || 1);
  return isoDate(date);
};

export const emptyKhata = () => ({
  expenseCount: 0,
  paymentCount: 0,
  totalBill: 0,
  totalPaid: 0,
  cashCount: 0,
  chequeCount: 0,
  onlineCount: 0,
  payOrderCount: 0,
  bankCount: 0,
  creditCount: 0,
  settledCount: 0,
  cash: 0,
  cheque: 0,
  online: 0,
  payOrder: 0,
  bank: 0,
  credit: 0,
  settled: 0,
  payable: 0,
});

const money = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export const emptyProjectKhata = () => ({
  expenseCount: 0,
  cashCount: 0,
  chequeCount: 0,
  onlineCount: 0,
  payOrderCount: 0,
  bankCount: 0,
  creditCount: 0,
  cash: 0,
  cheque: 0,
  online: 0,
  payOrder: 0,
  bank: 0,
  credit: 0,
  totalBill: 0,
  totalPaid: 0,
  payable: 0,
  saleCount: 0,
  activeSales: 0,
  completedSales: 0,
  totalSale: 0,
  downPayment: 0,
  received: 0,
  remaining: 0,
  cashIn: 0,
  cashOut: 0,
  cashNet: 0,
});

export const withProjectKhata = (khata = {}) => {
  const src = khata || {};
  const cash = money(src.cash);
  const cheque = money(src.cheque);
  const online = money(src.online);
  const payOrder = money(src.payOrder);
  const bank = money(src.bank);
  const credit = money(src.credit);
  const received = money(src.received);
  const totalSale = money(src.totalSale);
  const moneyOut = money(cash + cheque + online + payOrder + bank);
  const totalPaid = src.totalPaid != null ? money(src.totalPaid) : moneyOut;
  const cashIn = src.cashIn != null ? money(src.cashIn) : received;
  const cashOut = src.cashOut != null ? money(src.cashOut) : totalPaid;
  return {
    ...emptyProjectKhata(),
    ...src,
    cash,
    cheque,
    online,
    payOrder,
    bank,
    credit,
    totalBill: src.totalBill != null ? money(src.totalBill) : money(moneyOut + credit),
    totalPaid,
    payable: src.payable != null ? money(src.payable) : credit,
    saleCount: Number(src.saleCount || 0),
    activeSales: Number(src.activeSales || 0),
    completedSales: Number(src.completedSales || 0),
    totalSale,
    downPayment: money(src.downPayment),
    received,
    remaining: src.remaining != null ? money(src.remaining) : money(Math.max(0, totalSale - received)),
    cashIn,
    cashOut,
    cashNet: src.cashNet != null ? money(src.cashNet) : money(cashIn - cashOut),
    expenseCount: Number(src.expenseCount || 0),
    cashCount: Number(src.cashCount || 0),
    chequeCount: Number(src.chequeCount || 0),
    onlineCount: Number(src.onlineCount || 0),
    payOrderCount: Number(src.payOrderCount || 0),
    bankCount: Number(src.bankCount || 0),
    creditCount: Number(src.creditCount || 0),
  };
};

export const withKhata = (khata = {}) => {
  const src = khata || {};
  const cash = money(src.cash);
  const cheque = money(src.cheque);
  const online = money(src.online);
  const payOrder = money(src.payOrder);
  const bank = money(src.bank);
  const credit = money(src.credit);
  const settled = money(src.settled);
  const moneyPaid = money(cash + cheque + online + payOrder + bank);
  return {
    ...emptyKhata(),
    ...src,
    cash,
    cheque,
    online,
    payOrder,
    bank,
    credit,
    settled,
    payable: src.payable != null ? money(src.payable) : money(Math.max(0, credit - settled)),
    totalBill: src.totalBill != null ? money(src.totalBill) : money(moneyPaid + credit),
    totalPaid: src.totalPaid != null ? money(src.totalPaid) : money(moneyPaid + settled),
    expenseCount: Number(src.expenseCount || 0),
    paymentCount: Number(src.paymentCount || 0),
    cashCount: Number(src.cashCount || 0),
    chequeCount: Number(src.chequeCount || 0),
    onlineCount: Number(src.onlineCount || 0),
    payOrderCount: Number(src.payOrderCount || 0),
    bankCount: Number(src.bankCount || 0),
    creditCount: Number(src.creditCount || 0),
    settledCount: Number(src.settledCount || 0),
  };
};
